import { useEffect, useState, useCallback, useRef } from 'react';
import {
  productService,
  ProductItem,
  BoxTemplate,
  BoxTemplateParams,
  BoxTemplateResponse,
} from '../../services/product.service';
import ProductCard from '../../components/products/ProductCard';
import { showSuccess } from '../../utils/notificationUtils';
import { Breadcrumb, Spin, message } from 'antd';
import { Link } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';

// Mapper function to convert BoxTemplate to ProductItem
const mapBoxTemplateToProductItem = (boxTemplate: BoxTemplate): ProductItem => {
  return {
    id: boxTemplate._id,
    name: boxTemplate.name,
    description: boxTemplate.description,
    category: boxTemplate.type,
    images: boxTemplate.images,
    unit: 'box',
    original_price: boxTemplate.initial_price,
    current_price: boxTemplate.initial_price,
    is_on_sale: false,
    is_trending: false,
    slug: boxTemplate.slug,
    status: boxTemplate.status,
  };
};

const SpecialOrderProducts = () => {
  const [templates, setTemplates] = useState<BoxTemplate[]>([]);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(12);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);

  // Ref for intersection observer
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Fetch special order products
  const fetchSpecialOrderProducts = useCallback(
    async (page: number, append: boolean = false) => {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const params: BoxTemplateParams = {
          page,
          limit: pageSize,
        };

        const response = await productService.getSpecialOrderProducts(params);

        const newTemplates = response.box_templates;
        const newProducts = newTemplates.map(mapBoxTemplateToProductItem);

        if (append) {
          setTemplates((prev) => [...prev, ...newTemplates]);
          setProducts((prev) => [...prev, ...newProducts]);
        } else {
          setTemplates(newTemplates);
          setProducts(newProducts);
        }

        setTotal(response.pagination.total);
        // Update hasMore based on current page and total pages
        setHasMore(page < response.pagination.total_pages);
      } catch (error) {
        console.error('Error fetching special order products:', error);
        message.error('Không thể tải sản phẩm');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pageSize]
  );

  // Initial load of products
  useEffect(() => {
    setCurrentPage(1);
    fetchSpecialOrderProducts(1, false);
  }, [fetchSpecialOrderProducts]);

  // Load more products when scrolling
  const loadMoreProducts = useCallback(() => {
    if (loadingMore || !hasMore) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchSpecialOrderProducts(nextPage, true);
  }, [currentPage, fetchSpecialOrderProducts, hasMore, loadingMore]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px', // Add some margin to trigger earlier
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
        loadMoreProducts();
      }
    }, options);

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadMoreProducts, loadingMore, loading]);

  return (
    <div className='bg-gray-50 min-h-screen py-6'>
      <div className='container mx-auto px-4'>
        {/* Breadcrumb */}
        <Breadcrumb
          className='p-3 bg-white shadow-sm rounded-lg border border-gray-100 mb-6'
          separator={<span className='text-gray-400 mx-1'>/</span>}
          items={[
            {
              title: (
                <Link
                  to='/'
                  className='flex items-center text-gray-600 hover:text-primary transition-colors'
                >
                  <HomeOutlined className='mr-1' />
                  <span>Trang chủ</span>
                </Link>
              ),
            },
            {
              title: (
                <span className='font-medium text-primary'>
                  Hộp quà trái cây cắt sẵn
                </span>
              ),
            },
          ]}
        />

        {/* Page Title */}
        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8'>
          <div>
            <h1 className='text-2xl font-bold text-primary relative inline-block'>
              Hộp quà trái cây cắt sẵn
              <span className='absolute -bottom-1 left-0 right-0 h-1 bg-primary/20 rounded-full'></span>
            </h1>
            <p className='text-gray-600 text-sm mt-1'>
              Lựa chọn các gói trái cây đặc biệt đa dạng
            </p>
          </div>
          <div className='mt-4 sm:mt-0'>
            <p className='text-gray-500'>
              {loading && currentPage === 1
                ? 'Đang tải sản phẩm...'
                : `Hiển thị ${products.length} trong số ${total} sản phẩm`}
            </p>
          </div>
        </div>

        {/* Products Grid */}
        {loading && currentPage === 1 ? (
          <div className='flex justify-center items-center py-12'>
            <Spin size='large' />
          </div>
        ) : products.length === 0 ? (
          <div className='text-center py-12'>
            <p className='text-gray-500'>Chưa có sản phẩm</p>
          </div>
        ) : (
          <>
            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3'>
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  hideAddToCart={true}
                  customUrl={`/custom-box/${product.slug}`}
                />
              ))}
            </div>

            {/* Load more indicator */}
            <div ref={loadMoreRef} className='mt-8 py-4 text-center'>
              {loadingMore && (
                <div className='flex justify-center'>
                  <Spin size='default' />
                  <span className='ml-2 text-gray-500'>
                    Đang tải thêm sản phẩm...
                  </span>
                </div>
              )}
              {!hasMore && products.length > 0 && (
                <p className='text-gray-500'>Đã hiển thị tất cả sản phẩm</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SpecialOrderProducts;
